import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import React from 'react';
import {Link} from 'react-router-dom';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {ViewField} from 'src/design/atoms/ViewField';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {useGetOshaEstablishmentInformationQuery} from 'src/redux/queries/osha';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {NavAnchor} from 'src/design/atoms/NavAnchor';

interface FieldConfig {
  name:
    | 'establishmentName'
    | 'street'
    | 'city'
    | 'state'
    | 'zip'
    | 'industryDescription'
    | 'sicCode'
    | 'naicsCode';
  label: string;
  span: number;
}

const FIELD_CONFIGS: FieldConfig[] = [
  {name: 'establishmentName', label: 'Establishment Name', span: 12},
  {name: 'street', label: 'Street', span: 12},
  {name: 'city', label: 'City', span: 6},
  {name: 'state', label: 'State', span: 6},
  {name: 'zip', label: 'ZIP', span: 6},
  {name: 'industryDescription', label: 'Industry Description', span: 12},
  {name: 'sicCode', label: 'SIC Code', span: 6},
  {name: 'naicsCode', label: 'NAICS Code', span: 6},
];

export interface OshaEstablishmentCardProps {
  projectId: number;
}

export const OshaEstablishmentCard: React.FC<OshaEstablishmentCardProps> = ({
  projectId,
}) => {
  const projectPerms = useModulePermissions(ModuleEnum.Projects);

  const {
    data: establishment,
    isLoading,
    isError,
  } = useGetOshaEstablishmentInformationQuery(projectId);

  if (isLoading) {
    return (
      <SectionCard title="Establishment Information">
        <Box p="md">
          <Spinner loadingText="Loading establishment information..." />
        </Box>
      </SectionCard>
    );
  }

  if (isError || !establishment) {
    return (
      <SectionCard title="Establishment Information">
        <Box p="md">
          <ErrorAlert message="Establishment information could not be loaded. Please try again." />
        </Box>
      </SectionCard>
    );
  }

  const editAction = projectPerms.update ? (
    <NavAnchor
      component={Link}
      to={PathUtility.ProjectEdit(projectId)}
      text="Edit Project"
    />
  ) : undefined;

  return (
    <SectionCard title="Establishment Information" rightSection={editAction}>
      <Box p="md">
        <Grid gutter="md">
          {FIELD_CONFIGS.map(f => (
            <Grid.Col key={f.name} span={{base: 12, sm: f.span}}>
              <ViewField label={f.label} value={establishment[f.name]} />
            </Grid.Col>
          ))}
        </Grid>
      </Box>
    </SectionCard>
  );
};
